import { Box, Flex, Heading, Spinner, Table, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { api } from '../api';
import { StateBadge, StatusBadge } from '../components/Badges';
import { SiteWarehouseConfig } from '../components/SiteWarehouseConfig';
import { DeviceSoftwareReport } from '../types';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box borderWidth='1px' borderColor='card.border' bg='card.bg' borderRadius='lg' p={4}>
      <Heading size='sm' mb={3}>
        {title}
      </Heading>
      {children}
    </Box>
  );
}

/** Join host baseUrl with a path (nginx paths are absolute from the host root). */
function hostPath(baseUrl: string, path: string): string {
  const base = baseUrl.replace(/\/$/, '');
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a className='external-link' href={href} target='_blank' rel='noopener noreferrer'>
      {children}
    </a>
  );
}

function KeyValueTable({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <Table.Root size='sm' variant='line'>
      <Table.Body>
        {rows.map(([key, value]) => (
          <Table.Row key={key}>
            <Table.Cell fontWeight='medium' width='40%'>
              {key}
            </Table.Cell>
            <Table.Cell>{value}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

/** Shown in the Database section instead. */
const ENV_KEYS_SHOWN_IN_DATABASE = new Set(['DB_MANAGED', 'DB_SSL']);

function healthStatusColor(status: string): string {
  if (status === 'up') {
    return 'green.500';
  }
  if (status === 'skipped') {
    return 'gray.500';
  }
  return 'red.500';
}

/** `1.2.3@a1b2c3d`, or the image tag as published (`container-walls-1.0.1@a1b2c3d`). */
function softwareLabel(report: DeviceSoftwareReport): string {
  if (report.source === 'ecr' && report.version) {
    return report.commit ? `${report.version}@${report.commit}` : report.version;
  }
  if (report.source === 'local-build') {
    const branch = report.branch || 'unknown';
    const commit = report.commit || 'unknown';
    return `${branch}@${commit}${report.dirty ? ' (dirty)' : ''}`;
  }
  return report.commit || 'unknown';
}

function formatCheckIn(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function kindLabel(kind: DeviceSoftwareReport['kind']): string {
  return kind === 'station' ? 'Station' : 'Runner';
}

function DeviceSoftwareTable({ reports }: { reports: DeviceSoftwareReport[] }) {
  const sorted = [...reports].sort((a, b) => {
    const site = a.site_name.localeCompare(b.site_name);
    if (site !== 0) {
      return site;
    }
    if (a.kind !== b.kind) {
      return a.kind === 'station' ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  if (sorted.length === 0) {
    return <Text color='gray.500'>No station or runner has reported in the last 7 days.</Text>;
  }

  return (
    <Table.Root size='sm' variant='line'>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader>Device</Table.ColumnHeader>
          <Table.ColumnHeader>Kind</Table.ColumnHeader>
          <Table.ColumnHeader>Version</Table.ColumnHeader>
          <Table.ColumnHeader>Last check-in</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {sorted.map(report => (
          <Table.Row key={`${report.site_name}:${report.kind}:${report.name}`}>
            <Table.Cell>{report.name}</Table.Cell>
            <Table.Cell>{kindLabel(report.kind)}</Table.Cell>
            <Table.Cell>
              {softwareLabel(report)}
              {report.checkingIn === false ? (
                <Text as='span' color='orange.600' ml={2}>
                  not checking in
                </Text>
              ) : null}
            </Table.Cell>
            <Table.Cell>{formatCheckIn(report.reported_at)}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

function healthDetail(health: { adapterType?: string; database?: string; error?: string }): string {
  const parts = [health.adapterType, health.database, health.error].filter(Boolean);
  return parts.length ? parts.join(' — ') : '-';
}

export default function HostDetailPage() {
  const { name = '' } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['host', name],
    queryFn: () => api.hostDetail(name),
    enabled: Boolean(name),
  });

  if (isLoading) {
    return <Spinner />;
  }
  if (error || !data) {
    return <Text color='red.500'>Failed to load host: {(error as Error | undefined)?.message ?? 'not found'}</Text>;
  }

  const info = data.info?.data;
  const deployInfo = info?.deployInfo;
  const hostHomeUrl = hostPath(data.baseUrl, '/');
  const managementConsoleUrl = hostPath(data.baseUrl, '/management-console/');
  const fleetManagerUrl = hostPath(data.baseUrl, '/fleet-manager/');
  const siteNames = info?.sites.map(site => site.site_name) ?? data.sites;

  return (
    <Flex direction='column' gap={4} maxW='1100px'>
      <Flex align='center' gap={3}>
        <RouterLink to='/'>
          <Text color='primary.fg'>&larr; Overview</Text>
        </RouterLink>
        <Heading size='xl' flex={1}>
          {data.name}
        </Heading>
        <StateBadge state={data.state} />
        <StatusBadge status={data.status} />
      </Flex>

      <Section title='Quick links'>
        <Table.Root size='sm' variant='line'>
          <Table.Body>
            {siteNames.length > 0 ? (
              siteNames.map(siteName => (
                <Table.Row key={siteName}>
                  <Table.Cell fontWeight='medium' width='40%'>
                    {siteName} (host home)
                  </Table.Cell>
                  <Table.Cell>
                    <ExternalLink href={hostHomeUrl}>{hostHomeUrl}</ExternalLink>
                  </Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell fontWeight='medium' width='40%'>
                  Host home
                </Table.Cell>
                <Table.Cell>
                  <ExternalLink href={hostHomeUrl}>{hostHomeUrl}</ExternalLink>
                </Table.Cell>
              </Table.Row>
            )}
            <Table.Row>
              <Table.Cell fontWeight='medium'>Management Console</Table.Cell>
              <Table.Cell>
                <ExternalLink href={managementConsoleUrl}>{managementConsoleUrl}</ExternalLink>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell fontWeight='medium'>Fleet Manager</Table.Cell>
              <Table.Cell>
                <ExternalLink href={fleetManagerUrl}>{fleetManagerUrl}</ExternalLink>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      </Section>

      <Section title='Deployment'>
        <KeyValueTable
          rows={[
            ['Version', data.version ?? 'unknown (host not redeployed since deploy-info was added)'],
            ['Deployed at', typeof deployInfo?.deployedAt === 'string' ? deployInfo.deployedAt : 'unknown'],
            ['Deployed by', typeof deployInfo?.deployedBy === 'string' ? deployInfo.deployedBy : 'unknown'],
            [
              'Uptime (data-portal)',
              info
                ? `${Math.floor(info.uptimeSeconds / 3600)}h ${Math.floor((info.uptimeSeconds % 3600) / 60)}m`
                : 'unknown',
            ],
            ['Adapter', info?.env.values.ADAPTER_TYPE?.trim() || 'not configured'],
            ['Inventory fetched', data.info?.fetchedAt ?? 'never'],
          ]}
        />
      </Section>

      <Section title='Stations and runners'>
        <Text color='gray.500' mb={3}>
          Each device reports its image version and git commit every 15 minutes. One that has not checked in for an hour
          is marked not checking in, and stays listed for 7 days.
        </Text>
        <DeviceSoftwareTable reports={info?.deviceSoftware ?? []} />
      </Section>

      <Section title='Service health'>
        {data.health?.services ? (
          <Table.Root size='sm' variant='line'>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Service</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Latency</Table.ColumnHeader>
                <Table.ColumnHeader>Detail</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(data.health.services).map(([service, health]) => (
                <Table.Row key={service}>
                  <Table.Cell>{service}</Table.Cell>
                  <Table.Cell>
                    <Text color={healthStatusColor(health.status)} fontWeight='bold'>
                      {health.status}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>{health.latencyMs !== undefined ? `${health.latencyMs}ms` : '-'}</Table.Cell>
                  <Table.Cell>{healthDetail(health)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        ) : (
          <Text color='red.500'>
            {data.health
              ? data.health.nginxUp
                ? `Host reachable but data-portal is not answering (${data.health.error ?? 'unknown error'})`
                : `Host unreachable (${data.health.error ?? 'unknown error'})`
              : 'No health data yet'}
          </Text>
        )}
      </Section>

      <Section title={`Sites (${info?.sites.length ?? 0})`}>
        <Flex direction='column' gap={3}>
          {(info?.sites ?? []).map(site => (
            <SiteWarehouseConfig key={site.site_name} site={site} hostName={data.name} />
          ))}
          {!info?.sites.length ? <Text color='gray.500'>No sites reported</Text> : null}
        </Flex>
      </Section>

      <Section title='Database'>
        {info ? (
          <KeyValueTable
            rows={[
              ['Target', `${info.database.host}:${info.database.port}/${info.database.name}`],
              ['Managed (external)', String(info.database.managed)],
              ['SSL', String(info.database.ssl)],
              ['Connected', String(info.database.connected)],
            ]}
          />
        ) : (
          <Text color='gray.500'>No inventory data yet</Text>
        )}
      </Section>

      <Section title='TLS certificate'>
        <KeyValueTable
          rows={[
            ['Expires', data.tls?.validTo ?? data.tls?.error ?? 'unknown'],
            ['Days remaining', data.tls?.daysRemaining !== undefined ? String(data.tls.daysRemaining) : '-'],
            ['Issuer', data.tls?.issuer ?? '-'],
          ]}
        />
      </Section>

      <Section title='Environment'>
        {info ? (
          <Flex direction='column' gap={4}>
            <KeyValueTable
              rows={Object.entries(info.env.values)
                .filter(([key]) => !ENV_KEYS_SHOWN_IN_DATABASE.has(key))
                .map(([key, value]) => [key, value])}
            />
            <Box>
              <Text fontWeight='medium' mb={2}>
                Secrets (values never leave the host)
              </Text>
              <Flex gap={2} wrap='wrap'>
                {Object.entries(info.env.secretsPresent).map(([key, present]) => (
                  <Text
                    key={key}
                    fontSize='xs'
                    px={2}
                    py={1}
                    borderRadius='md'
                    bg={present ? 'green.subtle' : 'red.subtle'}
                  >
                    {key}: {present ? 'set' : 'missing'}
                  </Text>
                ))}
              </Flex>
            </Box>
          </Flex>
        ) : (
          <Text color='gray.500'>No inventory data yet</Text>
        )}
      </Section>
    </Flex>
  );
}
